import { supabase } from '../lib/supabase';
import type { Sport } from '../types';

/**
 * Get position group keys that are mapped to a custom position label
 */
export async function getPositionGroupsForLabel(
  teamId: string,
  sport: Sport,
  unit: 'Offense' | 'Defense' | 'Special Teams',
  positionLabel: string,
  formationId?: string,
  formationName?: string
): Promise<string[]> {
  try {
    // Position labels are stored in uppercase in the database
    const normalizedLabel = positionLabel.toUpperCase();
    
    console.log('[getPositionGroupsForLabel] Looking for mappings:', {
      teamId,
      sport,
      unit,
      positionLabel: normalizedLabel,
      formationId,
      formationName
    });
    
    // First try formation-specific mappings if we have formationId or formationName
    if (formationId || formationName) {
      let query = supabase
        .from('position_mappings')
        .select('position_group_key')
        .eq('team_id', teamId)
        .eq('sport', sport)
        .eq('unit', unit)
        .eq('position_label', normalizedLabel);

      if (formationId) {
        query = query.eq('formation_id', formationId);
      } else if (formationName) {
        query = query.eq('formation_name', formationName);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[getPositionGroupsForLabel] Error fetching formation-specific mappings:', error);
      } else if (data && data.length > 0) {
        const groups = (data || []).map((mapping: any) => mapping.position_group_key);
        console.log('[getPositionGroupsForLabel] Found formation-specific mappings:', groups);
        return groups;
      }
    }
    
    // Fallback to global mappings (no formation specified)
    const { data: globalData, error: globalError } = await supabase
      .from('position_mappings')
      .select('position_group_key')
      .eq('team_id', teamId)
      .eq('sport', sport)
      .eq('unit', unit)
      .eq('position_label', normalizedLabel)
      .is('formation_id', null)
      .is('formation_name', null);

    if (globalError) {
      console.error('[getPositionGroupsForLabel] Error fetching global mappings:', globalError);
      return [];
    }

    const globalGroups = (globalData || []).map((mapping: any) => mapping.position_group_key);
    console.log('[getPositionGroupsForLabel] Found global mappings:', globalGroups);
    
    return globalGroups;
  } catch (error) {
    console.error('Error in getPositionGroupsForLabel:', error);
    return [];
  }
}

/**
 * Check if a player can play a position based on position mappings
 */
export async function canPlayerPlayPosition(
  teamId: string,
  sport: Sport,
  unit: 'Offense' | 'Defense' | 'Special Teams',
  positionLabel: string,
  playerPositionGroups: string[],
  formationId?: string,
  formationName?: string
): Promise<boolean> {
  // If no mappings exist, allow all players (backward compatibility)
  const mappedGroups = await getPositionGroupsForLabel(
    teamId,
    sport,
    unit,
    positionLabel,
    formationId,
    formationName
  );

  // If no mappings found, allow all players
  if (mappedGroups.length === 0) {
    return true;
  }

  // Check if player has any of the mapped position groups
  return playerPositionGroups.some(group => mappedGroups.includes(group));
}

/**
 * Filter players based on position mappings
 * Also includes players with can_play_line: true when filtering for line positions (OL, DL, EDGE)
 */
export async function filterPlayersByPositionMapping(
  teamId: string,
  sport: Sport,
  unit: 'Offense' | 'Defense' | 'Special Teams',
  positionLabel: string,
  players: Array<{ id: string; eligible_positions?: string[]; can_play_line?: boolean }>,
  formationId?: string,
  formationName?: string
): Promise<Array<{ id: string; eligible_positions?: string[]; can_play_line?: boolean }>> {
  const mappedGroups = await getPositionGroupsForLabel(
    teamId,
    sport,
    unit,
    positionLabel,
    formationId,
    formationName
  );

  // If no mappings found, return all players (backward compatibility)
  if (mappedGroups.length === 0) {
    return players;
  }

  // Check if we're filtering for line positions
  const isLinePosition = mappedGroups.some(group => ['OL', 'DL', 'EDGE'].includes(group));

  // Filter players who have at least one of the mapped position groups
  // OR have can_play_line: true if filtering for line positions
  return players.filter(player => {
    const playerGroups = player.eligible_positions || [];
    const canPlayLine = player.can_play_line || false;
    
    // Check if player matches by position group
    const matchesByGroup = playerGroups.some(group => mappedGroups.includes(group));
    
    // If filtering for line positions, also include players with can_play_line: true
    const matchesByLineDesignation = isLinePosition && canPlayLine;
    
    return matchesByGroup || matchesByLineDesignation;
  });
}

/**
 * Get all position mappings for a formation
 */
export async function getFormationPositionMappings(
  teamId: string,
  sport: Sport,
  unit: 'Offense' | 'Defense' | 'Special Teams',
  formationId?: string,
  formationName?: string
): Promise<Record<string, string[]>> {
  try {
    let query = supabase
      .from('position_mappings')
      .select('position_label, position_group_key')
      .eq('team_id', teamId)
      .eq('sport', sport)
      .eq('unit', unit);

    if (formationId) {
      query = query.eq('formation_id', formationId);
    } else if (formationName) {
      query = query.eq('formation_name', formationName);
    } else {
      query = query.is('formation_id', null).is('formation_name', null);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching formation position mappings:', error);
      return {};
    }

    // Group by position label
    const mappings: Record<string, string[]> = {};
    (data || []).forEach((mapping: any) => {
      if (!mappings[mapping.position_label]) {
        mappings[mapping.position_label] = [];
      }
      if (!mappings[mapping.position_label].includes(mapping.position_group_key)) {
        mappings[mapping.position_label].push(mapping.position_group_key);
      }
    });

    return mappings;
  } catch (error) {
    console.error('Error in getFormationPositionMappings:', error);
    return {};
  }
}

